import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { apiService, Tenant } from '../../services/api';

const TenantManagement: React.FC = () => {
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
    const [formData, setFormData] = useState({ name: '', slug: '', is_active: true });
    const [submitting, setSubmitting] = useState(false);

    const fetchTenants = async () => {
        try {
            setLoading(true);
            const data = await apiService.getTenants();
            setTenants(data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch tenants');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTenants();
    }, []);

    const handleOpenDialog = (tenant?: Tenant) => {
        if (tenant) {
            setEditingTenant(tenant);
            setFormData({ name: tenant.name, slug: tenant.slug || '', is_active: tenant.is_active });
        } else {
            setEditingTenant(null);
            setFormData({ name: '', slug: '', is_active: true });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTenant(null);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            if (editingTenant) {
                await apiService.updateTenant(editingTenant.tenant_id, formData);
            } else {
                await apiService.createTenant(formData);
            }
            handleCloseDialog();
            fetchTenants();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to save tenant');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (tenant: Tenant) => {
        try {
            await apiService.updateTenant(tenant.tenant_id, { is_active: !tenant.is_active });
            fetchTenants();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to update tenant status');
        }
    };

    if (loading && tenants.length === 0) return <CircularProgress />;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Tenant Management</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Tenant
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Slug</strong></TableCell>
                            <TableCell><strong>Active</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tenants.map((tenant) => (
                            <TableRow key={tenant.tenant_id}>
                                <TableCell>{tenant.name}</TableCell>
                                <TableCell>{tenant.slug}</TableCell>
                                <TableCell>
                                    <Switch
                                        checked={tenant.is_active}
                                        onChange={() => handleToggleActive(tenant)}
                                        color="primary"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        startIcon={<EditIcon />}
                                        onClick={() => handleOpenDialog(tenant)}
                                    >
                                        Edit
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {tenants.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No tenants found.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingTenant ? 'Edit Tenant' : 'Add New Tenant'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Tenant Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <TextField
                            label="Tenant Slug"
                            fullWidth
                            required
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>Active:</Typography>
                            <Switch
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!formData.name || !formData.slug || submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TenantManagement;
